<template>
  <div class="dashboard-page animate-fade-in-up">
    <PageHeader icon="el-icon-s-grid" title="全团队离职风险分布概览" subtitle="实时监控团队人员风险状态，优先关注高风险员工" />

    <!-- 统计卡片 -->
    <div class="stats-row">
      <div class="stat-card stat-high">
        <div class="stat-bar"></div>
        <div class="stat-content">
          <div class="stat-label">高风险</div>
          <div class="stat-value">{{ highRisk.length }}</div>
          <div class="stat-desc">需立即介入</div>
        </div>
        <i class="el-icon-warning stat-icon"></i>
      </div>
      <div class="stat-card stat-medium">
        <div class="stat-bar"></div>
        <div class="stat-content">
          <div class="stat-label">中风险</div>
          <div class="stat-value">{{ mediumRisk.length }}</div>
          <div class="stat-desc">需持续关注</div>
        </div>
        <i class="el-icon-warning-outline stat-icon"></i>
      </div>
      <div class="stat-card stat-low">
        <div class="stat-bar"></div>
        <div class="stat-content">
          <div class="stat-label">低风险</div>
          <div class="stat-value">{{ lowRisk.length }}</div>
          <div class="stat-desc">状态稳定</div>
        </div>
        <i class="el-icon-circle-check stat-icon"></i>
      </div>
    </div>

    <!-- 风险看板 -->
    <div class="risk-board">
      <div class="risk-column">
        <div class="risk-column-header risk-header-high">
          <span class="risk-column-title">
            <i class="el-icon-warning"></i> 高风险
          </span>
          <span class="risk-column-count">{{ highRisk.length }} 人</span>
        </div>
        <div class="risk-column-body">
          <div v-if="highRisk.length === 0" class="risk-empty">
            <i class="el-icon-s-data"></i>
            <div>当前区间暂无人员</div>
          </div>
          <EmployeeCard v-for="emp in highRisk" :key="emp.employee_id" :employee="emp" color="red" />
        </div>
      </div>
      <div class="risk-column">
        <div class="risk-column-header risk-header-medium">
          <span class="risk-column-title">
            <i class="el-icon-warning-outline"></i> 中等风险
          </span>
          <span class="risk-column-count">{{ mediumRisk.length }} 人</span>
        </div>
        <div class="risk-column-body">
          <div v-if="mediumRisk.length === 0" class="risk-empty">
            <i class="el-icon-s-data"></i>
            <div>当前区间暂无人员</div>
          </div>
          <EmployeeCard v-for="emp in mediumRisk" :key="emp.employee_id" :employee="emp" color="orange" />
        </div>
      </div>
      <div class="risk-column">
        <div class="risk-column-header risk-header-low">
          <span class="risk-column-title">
            <i class="el-icon-circle-check"></i> 低风险 / 稳定
          </span>
          <span class="risk-column-count">{{ lowRisk.length }} 人</span>
        </div>
        <div class="risk-column-body">
          <div v-if="lowRisk.length === 0" class="risk-empty">
            <i class="el-icon-s-data"></i>
            <div>当前区间暂无人员</div>
          </div>
          <EmployeeCard v-for="emp in lowRisk" :key="emp.employee_id" :employee="emp" color="green" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EmployeeCard from '../components/EmployeeCard.vue'
import PageHeader from '../components/PageHeader.vue'

export default {
  name: 'Dashboard',
  components: { EmployeeCard, PageHeader },
  computed: {
    thresholds() {
      return this.$store.state.settings.settings.thresholds || { lowMax: 4, mediumMax: 10 }
    },
    employees() {
      const user = this.$store.state.user
      const email = user && user.email ? user.email : ''
      let list = []
      if (email) {
        list = this.$store.getters.subordinatesByEmail(email) || []
      } else {
        list = this.$store.getters.taskEmployees || []
      }
      // 只显示在职且已评估的员工
      return list.filter(e => e.employment_status === 1 && e.latest_risk_level && e.latest_score > 0)
    },
    highRisk() {
      return this.employees.filter(e => e.latest_risk_level === 3).sort((a, b) => b.latest_score - a.latest_score)
    },
    mediumRisk() {
      return this.employees.filter(e => e.latest_risk_level === 2).sort((a, b) => b.latest_score - a.latest_score)
    },
    lowRisk() {
      return this.employees.filter(e => e.latest_risk_level === 1).sort((a, b) => a.latest_score - b.latest_score)
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  max-width: 1400px;
  margin: 0 auto;
}
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-lg);
  margin-bottom: var(--space-2xl);
}
.stat-card {
  display: flex;
  align-items: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  box-shadow: var(--shadow-sm);
  position: relative;
  overflow: hidden;
  transition: all var(--duration-normal) var(--ease-out);
}
.stat-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}
.stat-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}
.stat-high .stat-bar { background: var(--color-high); }
.stat-medium .stat-bar { background: var(--color-medium); }
.stat-low .stat-bar { background: var(--color-low); }
.stat-content {
  flex: 1;
  padding-left: var(--space-md);
}
.stat-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-xs);
}
.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1;
}
.stat-desc {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: var(--space-xs);
}
.stat-icon {
  font-size: 32px;
  color: var(--color-text-muted);
  opacity: 0.3;
}

.risk-board {
  display: flex;
  gap: var(--space-lg);
  overflow-x: auto;
}
.risk-column {
  flex: 1;
  min-width: 300px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}
.risk-column-header {
  padding: var(--space-md) var(--space-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.risk-header-high { background: var(--color-high-bg); }
.risk-header-medium { background: var(--color-medium-bg); }
.risk-header-low { background: var(--color-low-bg); }
.risk-column-title {
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}
.risk-header-high .risk-column-title { color: var(--color-high-text); }
.risk-header-medium .risk-column-title { color: var(--color-medium-text); }
.risk-header-low .risk-column-title { color: var(--color-low-text); }
.risk-column-count {
  font-weight: 600;
  font-size: 13px;
}
.risk-header-high .risk-column-count { color: var(--color-high); }
.risk-header-medium .risk-column-count { color: var(--color-medium); }
.risk-header-low .risk-column-count { color: var(--color-low); }
.risk-column-body {
  padding: var(--space-md);
  min-height: 300px;
}
.risk-empty {
  text-align: center;
  padding: var(--space-3xl) 0;
  color: var(--color-text-muted);
}
.risk-empty i {
  font-size: 48px;
  margin-bottom: var(--space-md);
  display: block;
}
</style>
